import { Card, CardProps, useTheme, Box, Typography, useMediaQuery } from '@mui/material';
import React from 'react';
import { getCardStyles } from '../config/theme';

interface PortalCardProps extends Omit<CardProps, 'variant'> {
    children: React.ReactNode;
}

/**
 * Standardized card component for Portal screens
 * Styling controlled globally in theme.ts → PortalUIConfig.card.shadow
 */
export const PortalCard: React.FC<PortalCardProps> = ({ 
    children, 
    sx = {},
    ...props 
}) => {
    const theme = useTheme();

    return (
        <Card
            sx={getCardStyles(theme, sx)}
            {...props}
        >
            {children}
        </Card>
    );
};

/**
 * Stat card with icon - pre-configured for statistics display
 */
interface StatCardProps {
    icon?: React.ReactNode;
    iconColor?: string;
    iconBgColor?: string;
    title: string;
    value: string | number;
    subtitle?: string;
}

export const PortalStatCard: React.FC<StatCardProps> = ({
    icon,
    iconColor,
    iconBgColor,
    title,
    value,
    subtitle
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    // If iconBgColor is provided (solid background), default icon to white for contrast
    const displayIconColor = iconColor || (iconBgColor ? 'white' : theme.palette.primary.main);
    const displayIconBgColor = iconBgColor || `${theme.palette.primary.main}20`;

    return (
        <PortalCard 
            sx={{ 
                p: { xs: 2, sm: 3 },
                minHeight: { xs: 90, sm: 110 },
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignSelf: 'center',
            }}
        >
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'row',
                alignItems: 'center', 
                gap: { xs: 1.5, sm: 2 } 
            }}>
                {icon && (
                    <Box
                        sx={{
                            width: { xs: 40, sm: 48 },
                            height: { xs: 40, sm: 48 },
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: displayIconBgColor,
                            flexShrink: 0,
                        }}
                    >
                        {React.cloneElement(icon as React.ReactElement, {
                            sx: { 
                                color: displayIconColor,
                                fontSize: isMobile ? 20 : 24 
                            }
                        })}
                    </Box>
                )}
                
                <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                    <Typography 
                        variant='caption' 
                        color='text.secondary' 
                        sx={{ 
                            display: 'block', 
                            fontFamily: 'primaryFont', 
                            letterSpacing: { xs: 0.3, sm: 1 },
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            lineHeight: 1.2
                        }}
                    >
                        {title}
                    </Typography>
                    
                    <Typography 
                        variant='h5' 
                        color='text.primary'
                        fontFamily='primaryFont'
                        sx={{ 
                            wordBreak: 'break-word',
                            fontSize: { xs: '1.1rem', sm: '1.5rem' },
                            fontWeight: 600,
                            lineHeight: 1.15,
                            mt: 0.25
                        }}
                    >
                        {value}
                    </Typography>
                    
                    {subtitle && (
                        <Typography 
                            variant='caption' 
                            color='text.secondary'
                            fontFamily='primaryFont'
                            sx={{ 
                                display: 'block',
                                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                lineHeight: 1.2,
                                mt: 0.25
                            }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>
            </Box>
        </PortalCard>
    );
};

export default PortalCard;

